import {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {VERIFICATION_METHODS, VerificationMethod} from '@chekin/api';
import {
  AlertBox,
  AlertSizes,
  AlertTypes,
  ModalTwoButtonsWrapper,
} from '@guestapp/old-ui-kit';
import Button from 'components/common/Button';
import {CodeVerificationSection} from '../CodeVerificationSection';
import {
  CodesContainer,
  ButtonsContainer,
  ChangeContactsIcon,
  ChangeContactsText,
  ChangeContactsButton,
  CodesVerificationContainer,
} from './styled';

type Props = {
  title: string;
  sentToValue?: string;
  type: VerificationMethod;
  onVerifyCodes: (code: string) => Promise<void>;
  onResendCodes?: () => Promise<void>;
  onChangeContacts?: () => void;
  isLoading?: boolean;
};

const ContactsVerificationCodes = ({
  title,
  sentToValue,
  type,
  onVerifyCodes,
  onResendCodes,
  onChangeContacts,
  isLoading = false,
}: Props) => {
  const {t} = useTranslation();
  const [code, setCode] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const requiredLength = 6;

    if (code.length !== requiredLength) {
      setError(t('invalid_code_length', {length: requiredLength}));
      return;
    }

    try {
      setError('');
      await onVerifyCodes(code);
    } catch {
      setError(t('invalid_verification_code'));
    }
  };

  const handleResendCodes = async () => {
    if (onResendCodes) {
      try {
        await onResendCodes();
      } catch {
        setError(t('failed_to_resend_code'));
      }
    }
  };

  const isFormValid = () => {
    return code.length === 6;
  };

  return (
    <CodesVerificationContainer>
      <AlertBox
        text={t('code_expires_in', {minutes: 2})}
        type={AlertTypes.INFO}
        size={AlertSizes.S}
      />
      <CodesContainer>
        <CodeVerificationSection
          title={title}
          subtitle={t('sent_to', {value: sentToValue})}
          value={code}
          onChange={setCode}
          errorMessage={error}
          length={6}
        />
      </CodesContainer>

      <ButtonsContainer>
        <ModalTwoButtonsWrapper>
          <Button
            type="submit"
            label={t('verify_code')}
            disabled={!isFormValid() || isLoading}
            loading={isLoading}
            onClick={handleSubmit}
          />

          <Button
            label={t('resend_code')}
            onClick={handleResendCodes}
            disabled={isLoading}
            outlined
          />
        </ModalTwoButtonsWrapper>

        {onChangeContacts && (
          <ChangeContactsButton onClick={onChangeContacts} disabled={isLoading}>
            <ChangeContactsIcon size={24} color="#385BF8" />
            <ChangeContactsText>
              {type === VERIFICATION_METHODS.email
                ? t('change_email')
                : t('change_phone_number')}
            </ChangeContactsText>
          </ChangeContactsButton>
        )}
      </ButtonsContainer>
    </CodesVerificationContainer>
  );
};

export {ContactsVerificationCodes};
